/**
 * Shared filter context for the Banking section (Balances / Transactions /
 * Reconciliation tabs).
 *
 * Persists entity selection and drill-through filters (account, transaction)
 * in URL query params so deep links reproduce the full filter state.
 * Tab switches preserve filters; clearing a drill-through filter removes
 * its query param.
 *
 * txEntityFilter / txBankFilter are the Transactions-tab-local entity/bank
 * filters (distinct from the sidebar's global entity scope -- these add an
 * "Unattributed" option the sidebar selector doesn't have). Persisted here,
 * not page-local state, specifically so BankingPage's tab-switch preserves
 * location.search and they carry through to the Reconciliation tab's
 * transaction-side query for free.
 *
 * reconTab is the Reconciliation page's own sub-tab (Suggestions / Recurring
 * costs / Unreconciled transactions), deep-linkable the same way -- carried
 * here rather than a local useSearchParams call in ReconciliationPage.jsx so
 * that page (already a hard dependent of this provider) gets URL-backed tab
 * state for free, and every existing reconciliation test's mocked
 * useBankingFilter() (which omits reconTab) keeps defaulting to Suggestions.
 */

import { createContext, useContext, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

var BankingFilterContext = createContext(null);

export function BankingFilterProvider({ children }) {
  var [searchParams, setSearchParams] = useSearchParams();

  // Read filter values from URL query params
  var accountFilter = searchParams.get('account') || '';
  var transactionFilter = searchParams.get('transaction') || '';
  var accountLabel = searchParams.get('accountLabel') || '';
  var txEntityFilter = searchParams.get('tx_entity') || '';
  var txBankFilter = searchParams.get('tx_bank') || '';
  var reconTab = searchParams.get('recon_tab') || '';

  var setAccountFilter = useCallback(function (accountId, label) {
    setSearchParams(function (prev) {
      var next = new URLSearchParams(prev);
      if (accountId) {
        next.set('account', accountId);
        if (label) next.set('accountLabel', label);
      } else {
        next.delete('account');
        next.delete('accountLabel');
      }
      return next;
    });
  }, [setSearchParams]);

  var setTransactionFilter = useCallback(function (transactionId) {
    setSearchParams(function (prev) {
      var next = new URLSearchParams(prev);
      if (transactionId) {
        next.set('transaction', transactionId);
      } else {
        next.delete('transaction');
      }
      return next;
    });
  }, [setSearchParams]);

  var setTxEntityFilter = useCallback(function (entityId) {
    setSearchParams(function (prev) {
      var next = new URLSearchParams(prev);
      if (entityId && entityId !== 'all') {
        next.set('tx_entity', entityId);
      } else {
        next.delete('tx_entity');
      }
      return next;
    });
  }, [setSearchParams]);

  var setTxBankFilter = useCallback(function (bankName) {
    setSearchParams(function (prev) {
      var next = new URLSearchParams(prev);
      if (bankName && bankName !== 'all') {
        next.set('tx_bank', bankName);
      } else {
        next.delete('tx_bank');
      }
      return next;
    });
  }, [setSearchParams]);

  var setReconTab = useCallback(function (tab) {
    setSearchParams(function (prev) {
      var next = new URLSearchParams(prev);
      if (tab && tab !== 'suggestions') {
        next.set('recon_tab', tab);
      } else {
        next.delete('recon_tab');
      }
      return next;
    });
  }, [setSearchParams]);

  var clearDrillThrough = useCallback(function () {
    setSearchParams(function (prev) {
      var next = new URLSearchParams(prev);
      next.delete('account');
      next.delete('accountLabel');
      next.delete('transaction');
      return next;
    });
  }, [setSearchParams]);

  var value = useMemo(function () {
    return {
      accountFilter: accountFilter,
      accountLabel: accountLabel,
      transactionFilter: transactionFilter,
      txEntityFilter: txEntityFilter,
      txBankFilter: txBankFilter,
      reconTab: reconTab,
      setAccountFilter: setAccountFilter,
      setTransactionFilter: setTransactionFilter,
      setTxEntityFilter: setTxEntityFilter,
      setTxBankFilter: setTxBankFilter,
      setReconTab: setReconTab,
      clearDrillThrough: clearDrillThrough,
    };
  }, [
    accountFilter, accountLabel, transactionFilter, txEntityFilter, txBankFilter, reconTab,
    setAccountFilter, setTransactionFilter, setTxEntityFilter, setTxBankFilter, setReconTab, clearDrillThrough,
  ]);

  return (
    <BankingFilterContext.Provider value={value}>
      {children}
    </BankingFilterContext.Provider>
  );
}

export function useBankingFilter() {
  var ctx = useContext(BankingFilterContext);
  if (!ctx) {
    throw new Error('useBankingFilter must be used within a BankingFilterProvider');
  }
  return ctx;
}
